import { useRecoilValue } from 'recoil';

import { Heading } from '@/components/heading';
import { Text } from '@/components/text';
import { LoadingIndicator } from '@/components/ui/loading/indicator';
import { getAverageRate } from '@/lib/models/rates/get-average-rate';
import { getSpotRatesByRouteState, spotRateSearchState } from '@/state/rates';
import { formatPrice } from '@/utils/format';
import { tv } from '@/utils/styles';

import { OptionSummaryCard } from '..';

const legalText = 'Avg. per TEU, not including local fees & surcharges';
const noResultsText = 'No current rates available for this route';

interface RatesSummaryCardProps {
  originId: string;
  destinationId: string;
  carrierIds?: string[];
}

const RatesSummaryCard = ({ originId, destinationId, carrierIds = [] }: RatesSummaryCardProps) => {
  const rates = useRecoilValue(getSpotRatesByRouteState({ pol: originId, pod: destinationId }));
  const { isSearching } = useRecoilValue(spotRateSearchState);
  const filteredRates = carrierIds.length ? rates.filter((rate) => carrierIds.includes(rate.carrierId)) : rates;
  const averageRate = getAverageRate(filteredRates);

  const { cardHeading, cardHeadingSmall, cardTextLegal } = styles();

  if (isSearching) {
    return (
      <OptionSummaryCard title="Average spot rate" className="bg-softPurple">
        <LoadingIndicator className="my-2 mr-0" darkTheme />
        <Text className={cardTextLegal({ className: 'italic' })}>{legalText}</Text>
      </OptionSummaryCard>
    );
  }

  return (
    <OptionSummaryCard title="Average spot rate" className="bg-softPurple">
      {!averageRate ? (
        <>
          <Heading as="h4" variant="h3" className={cardHeading()}>
            --
          </Heading>
          <Text className={cardTextLegal({ className: 'italic' })}>{noResultsText}</Text>
        </>
      ) : (
        <>
          <Heading as="h4" variant="h3" className={cardHeading()}>
            {formatPrice(averageRate.value, averageRate.currency, 1)}
            <small className={cardHeadingSmall()}> USD</small>
          </Heading>
          <Text className={cardTextLegal({ className: 'italic' })}>{legalText}</Text>
        </>
      )}
    </OptionSummaryCard>
  );
};

const styles = tv({
  slots: {
    cardHeading: 'mb-1',
    cardHeadingSmall: 'text-base',
    cardTextLegal: 'm-0 text-xs opacity-80',
  },
});

export { RatesSummaryCard };
